/**
 * File upload API functions
 */
import { getRuntimeConfigSync, loadRuntimeConfig } from "@/lib/runtime-config";
import type { components } from "./types.generated";

type Schemas = components["schemas"];

/**
 * Uploaded file response (re-exported from generated OpenAPI schema).
 */
export type UploadedFile = Schemas["UploadedFile"];

/**
 * Upload a file. The API returns an envelope `{status, data}`; we unwrap
 * `data` inline so callers receive the typed `UploadedFile` directly.
 */
export const uploadFile = async (file: File): Promise<UploadedFile> => {
  const formData = new FormData();
  formData.append("file", file);

  const { apiBaseUrl } = await loadRuntimeConfig();
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("yuklar_access_token")
      : null;

  const response = await fetch(`${apiBaseUrl}/files`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Upload failed" }));
    throw new Error(error.message || "Upload failed");
  }

  const json = (await response.json()) as Schemas["UploadedFileResponse"];
  return json.data;
};

/**
 * Get file URL
 */
export const getFileUrl = (
  fileId: string,
  download: boolean = false,
): string => {
  const { apiBaseUrl } = getRuntimeConfigSync();
  return `${apiBaseUrl}/files/${fileId}/content${download ? "?download=true" : ""}`;
};

/**
 * Get thumbnail URL
 */
export const getThumbnailUrl = (thumbnailId: string): string => {
  const { apiBaseUrl } = getRuntimeConfigSync();
  return `${apiBaseUrl}/files/${thumbnailId}/thumbnail`;
};

/**
 * Fetch a file's content with the caller's access token.
 *
 * The `/files/{id}/content` endpoint is protected by `requireAuth`, which
 * only accepts the token via the `Authorization: Bearer` header. A plain
 * `<a href>` / new-tab navigation can't set that header, so it 401s with
 * `NO_TOKEN_PROVIDED`. We fetch the bytes ourselves and hand back a blob.
 */
const fetchFileBlob = async (
  fileId: string,
  download: boolean,
): Promise<{ blob: Blob; filename: string | null }> => {
  const { apiBaseUrl } = await loadRuntimeConfig();
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("yuklar_access_token")
      : null;

  const url = `${apiBaseUrl}/files/${fileId}/content${download ? "?download=true" : ""}`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch file ${fileId}: ${res.status}`);
  }
  return {
    blob: await res.blob(),
    filename: parseContentDispositionFilename(
      res.headers.get("content-disposition"),
    ),
  };
};

/**
 * Pull the `filename` out of a `Content-Disposition` header so blob
 * downloads keep the server-provided name/extension instead of a bare id.
 */
const parseContentDispositionFilename = (
  header: string | null,
): string | null => {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  return match ? decodeURIComponent(match[1].trim()) : null;
};

/**
 * Open a stored file in a new tab, authenticating via the access token.
 *
 * iOS Safari blocks window.open from async callbacks, so we open a blank
 * window synchronously (within the user gesture) then navigate it to the
 * blob URL after the fetch completes — same approach as `openAgreementPdf`.
 */
export const openFile = async (fileId: string): Promise<void> => {
  const win = window.open("about:blank", "_blank");
  try {
    const { blob, filename } = await fetchFileBlob(fileId, false);
    const blobUrl = URL.createObjectURL(blob);
    if (win) {
      win.location.href = blobUrl;
    } else {
      // Popup blocked — fall back to a download.
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename ?? fileId;
      a.click();
    }
  } catch (error) {
    win?.close();
    throw error;
  }
};

/**
 * Download a stored file, authenticating via the access token.
 */
export const downloadFile = async (fileId: string): Promise<void> => {
  const { blob, filename } = await fetchFileBlob(fileId, true);
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename ?? fileId;
  a.click();
  URL.revokeObjectURL(blobUrl);
};
